import TextField from '@mui/material/TextField';
import { MemreButton, MemreFlexBox } from 'components/core';
import { useEffect, useState } from 'react';
import { useAuthoringContext } from '../AuthoringContext';
import Sound from '../components/Sound';
import { useDistractors } from 'hooks/useDistractors';
import { TConcept } from 'types';
import { useCreateDistractor } from 'hooks/useCreateDistractor';
import { pick } from 'lodash';
import { AnswerField } from './AnswerField';
import { useDeleteConcept } from 'hooks/useDeleteConcept';
import SuggestedDistractors from './SuggestedDistractors';

interface IncorrectAnswersProps {
  /** Specifies the concept that the distractors will be attached to. */
  conceptIdForDistractors: string;
  backgroundColor?: string;
  title?: string;
  /** Question text used for generating suggested distractors */
  questionForSuggestedDistractors?: string;
  /** Correct answers used for generating suggested distractors */
  correctAnswersForSuggestedDistractors?: string[];
}

export function IncorrectAnswers(props: IncorrectAnswersProps) {
  const {
    conceptIdForDistractors,
    backgroundColor,
    title,
    questionForSuggestedDistractors,
    correctAnswersForSuggestedDistractors,
  } = props;
  const { set: assignment, item } = useAuthoringContext();
  const [incorrectAnswers, setIncorrectAnswers] = useState<TConcept[]>([]);
  const { data: distractors } = useDistractors(conceptIdForDistractors);
  const [createDistractor] = useCreateDistractor(item?.id || '', assignment?.id || '', conceptIdForDistractors);
  const [deleteConcept] = useDeleteConcept(assignment?.id || '', item?.id || '', conceptIdForDistractors);

  // TODO: sometimes a wrong answer appears and then disappears again in the UI
  function addAnswer(answer: TConcept) {
    setIncorrectAnswers([...incorrectAnswers, answer]);
  }
  function updateAnswer(conceptId: string, updates: Partial<TConcept>) {
    setIncorrectAnswers(incorrectAnswers.map((a) => (a.id === conceptId ? { ...a, ...updates } : a)));
  }
  async function removeAnswer(conceptId: string) {
    await deleteConcept(conceptId);
    setIncorrectAnswers(incorrectAnswers.filter((a) => a.id !== conceptId));
  }

  const getConceptFromDistractor = (distractor: any) => pick(distractor, ['id', 'text', 'image', 'sound']) as TConcept;
  const wrongAnswers: any[] = distractors?.map(getConceptFromDistractor) || [];

  useEffect(() => {
    setIncorrectAnswers([...wrongAnswers]);
  }, [distractors, item]);

  if (distractors === undefined) {
    return <div>Loading...</div>;
  }

  const createNewWrongAnswer = async (text?: string): Promise<void> => {
    if (!item) {
      console.warn('Item not found');
      return;
    }

    const textPayload: any = { attributes: { 'text-html': text } };

    // create a new answer in the database
    // new answers for nonsurveys are always created as distractors, i.e. as incorrect answers
    const distractor = await createDistractor({
      conceptId: conceptIdForDistractors,
      payload: text ? textPayload : null,
    });
    const formattedAnswer = getConceptFromDistractor(distractor);

    // create a new answer in the store
    addAnswer(formattedAnswer);
  };

  // Get all existing answer texts for the SuggestedDistractors component
  const existingAnswerTexts = incorrectAnswers.map((answer) => answer.text || '');

  return (
    <>
      <MemreFlexBox direction="column" gap={1} sx={{ width: '100%' }}>
        <MemreFlexBox direction="row" justify="space-between" sx={{ width: '100%' }}>
          <h4>{title ?? 'Wrong Answer Options'}</h4>
        </MemreFlexBox>
        {incorrectAnswers.map((wrongAnswer, index) => (
          <AnswerField
            key={wrongAnswer.id}
            concept={wrongAnswer}
            label={`Wrong answer ${index + 1}`}
            updateAnswerLocally={updateAnswer}
            didClickDelete={() => removeAnswer(wrongAnswer.id)}
            backgroundColor={backgroundColor}
            allowSound={false}
          />
        ))}
        <MemreButton
          variant="text"
          text="Add Wrong Answer"
          size="small"
          iconName="plus"
          onClick={() => createNewWrongAnswer()}
        />

        {/* Add SuggestedDistractors component if question and correct answers are provided */}
        {questionForSuggestedDistractors && correctAnswersForSuggestedDistractors && (
          <SuggestedDistractors
            questionText={questionForSuggestedDistractors}
            correctAnswers={correctAnswersForSuggestedDistractors}
            existingAnswers={existingAnswerTexts}
            onAddDistractor={createNewWrongAnswer}
            autoGenerate={false} // Set to false so user must click the generate button
          />
        )}
      </MemreFlexBox>
    </>
  );
}
